import React, { useState } from 'react';
import { specialEffect, workflowColour } from '../lib/format';
import type { Catalogue, Rule } from '../lib/types';

/**
 * Every rule the engine follows, as it served them.
 *
 * This screen is the clearest demonstration of the property the whole system
 * rests on: nothing below is written in this repository. Change a resolution
 * label on the server and it appears here on the next load.
 */
export function WorkflowMap({ catalogue }: { catalogue: Catalogue }): React.JSX.Element {
  const [open, setOpen] = useState<string | null>(null);

  const byWorkflow: Record<string, Rule[]> = {};
  for (const rule of catalogue.rules) (byWorkflow[rule.workflow] ??= []).push(rule);

  const branching = catalogue.rules.filter((r) => r.question).length;
  const special = catalogue.rules.filter((r) => r.special).length;

  return (
    <>
      <div className="note" style={{ marginBottom: 16 }}>
        <b>{catalogue.rules.length} rules, {branching} of them branching, {special} with side effects.</b>{' '}
        Fetched from <span className="mono">GET /v1/workflows</span>, not built into this console.
        A rule changed in the engine appears here on the next load, with no release of this app.
      </div>

      {Object.entries(byWorkflow).map(([workflow, rules]) => (
        <div key={workflow} style={{ marginBottom: 18 }}>
          <div className="between" style={{ marginBottom: 10 }}>
            <h2 style={{ fontSize: 15, color: workflowColour(workflow) }}>{workflow}</h2>
            <span className="sub">{rules.length} rules</span>
          </div>

          {rules.map((rule) => {
            const expanded = open === rule.name;
            return (
              <div className="card" key={rule.name} style={{ marginBottom: 9 }}>
                <button className="hd" style={{ width: '100%' }}
                  onClick={() => setOpen(expanded ? null : rule.name)}>
                  <span className="barc" style={{
                    width: 4, height: 22, borderRadius: 3, background: workflowColour(workflow),
                  }} />
                  <h3 style={{ fontSize: 13.5 }}>{rule.name}</h3>
                  <span className="r">
                    <span className={`tag t-${rule.type}`}>{rule.type}</span>
                    {rule.manualEntry && <span className="tag t-warn">entry point</span>}
                    {rule.special && <span className="tag t-info">{rule.special}</span>}
                    {rule.question && <span className="tag t-mute">{rule.question.options.length} branches</span>}
                    <span className="sub">{expanded ? '−' : '+'}</span>
                  </span>
                </button>

                {expanded && (
                  <div className="bd">
                    {rule.question ? (
                      <>
                        <div className="sub" style={{ marginBottom: 8 }}>
                          Asks the operator: <b style={{ color: 'var(--ink)' }}>{rule.question.prompt}</b>
                        </div>
                        <div className="chain">
                          {rule.question.options.map((option) => (
                            <div className="node" key={option.label}>
                              <b>{option.label}</b>
                              <span>
                                {option.next.length
                                  ? `→ ${option.next.join(', ')}`
                                  : '→ the chain ends here'}
                              </span>
                              {(option.pauseRent || option.resumeRent || option.createsLease) && (
                                <div style={{ marginTop: 4 }}>
                                  {option.pauseRent && <span className="tag t-bad">pauses rent</span>}
                                  {option.resumeRent && <span className="tag t-ok">resumes rent</span>}
                                  {option.createsLease && <span className="tag t-warn">creates a lease</span>}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="sub">
                        No question. {rule.special
                          ? `Completing it ${specialEffect(rule.special)}.`
                          : rule.next.length
                            ? `Completing it creates: ${rule.next.join(', ')}.`
                            : 'Completing it ends the chain.'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {Object.keys(catalogue.adhoc).length > 0 && (
        <>
          <h2 className="sec">Ad-hoc work</h2>
          <div className="note" style={{ marginBottom: 12 }}>
            These have no chain. They are created by hand, done, and finished — no successors.
          </div>
          <div className="grid g3">
            {Object.entries(catalogue.adhoc).map(([workflow, items]) => (
              <div className="card" key={workflow}>
                <div className="hd"><h3>{workflow}</h3><span className="r sub">{items.length}</span></div>
                <div className="bd">
                  {items.map((item) => (
                    <div className="between" key={item.name} style={{ padding: '4px 0', fontSize: 12.5 }}>
                      <span>{item.name}</span>
                      <span className={`tag t-${item.type}`}>{item.type}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
